import { useCallback, useState } from "react";
import type { DocumentNode, TypedDocumentNode } from "@apollo/client";
import { useQuery } from "@apollo/client/react";

// Cursor-based paging over genesis list queries. Every list connection here
// has the same shape – `{ nodes, pageInfo { hasNextPage, endCursor }, totalCount }`
// – and every list input accepts `first` + `after`, so one hook covers them
// all. Pass the connection's top-level field name (e.g. "documents",
// "persons", "sharedItems") and the base input WITHOUT first/after; filter
// changes in `input` reset paging automatically (new variables → fresh page 1).
//
// IMPORTANT: paged queries must select `pageInfo { startCursor endCursor }`
// (both!). genesis only attaches cursors to the edges named in the selection,
// but its createConnection() reads first AND last edge cursor unconditionally;
// selecting endCursor alone makes every page after the first fail with an
// INTERNAL_ERROR ("Field 'cursor' is not set").
//
// Pair with <LoadMore /> for the button + infinite-scroll sentinel.

interface PagedResult<T> {
  nodes: T[];
  totalCount: number | null;
  /** True while the FIRST page (or a filter change) is loading. */
  loading: boolean;
  /** True while a load-more (next page) is in flight. */
  loadingMore: boolean;
  hasMore: boolean;
  /** True when the last load-more failed. Pass to <LoadMore paused>. */
  loadMoreFailed: boolean;
  // Apollo v4 removed the ApolloError class; useQuery now returns ErrorLike.
  error: Error | undefined;
  loadMore: () => void;
  refetch: () => Promise<unknown>;
}

export function usePagedQuery<T = unknown>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  document: DocumentNode | TypedDocumentNode<any, any>,
  opts: {
    /** Top-level connection field on `data`, e.g. "documents". */
    connectionKey: string;
    /** Base input without `first`/`after`. Memoize it (filters reset paging). */
    input?: Record<string, unknown>;
    pageSize?: number;
    skip?: boolean;
  },
): PagedResult<T> {
  const { connectionKey, input = {}, pageSize = 24, skip } = opts;
  const { data, loading, error, fetchMore, refetch } = useQuery(document, {
    variables: { input: { ...input, first: pageSize } },
    skip,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const conn: any = (data as any)?.[connectionKey];
  const nodes: T[] = conn?.nodes ?? [];
  const endCursor: string | null = conn?.pageInfo?.endCursor ?? null;
  const hasMore = Boolean(conn?.pageInfo?.hasNextPage) && endCursor != null;
  const totalCount: number | null = conn?.totalCount ?? null;

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore || !endCursor) return;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    fetchMore({
      variables: { input: { ...input, first: pageSize, after: endCursor } },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const prevConn = (prev as any)?.[connectionKey];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const nextConn = (fetchMoreResult as any)?.[connectionKey];
        return {
          ...prev,
          [connectionKey]: {
            ...nextConn,
            nodes: [...(prevConn?.nodes ?? []), ...(nextConn?.nodes ?? [])],
          },
        };
      },
    })
      // Remember the failure so <LoadMore /> stops auto-firing from its
      // IntersectionObserver; otherwise a failing page request loops forever
      // (observer fires, request fails, button re-enables, observer fires...).
      .catch(() => setLoadMoreFailed(true))
      .finally(() => setLoadingMore(false));
  }, [hasMore, loadingMore, endCursor, fetchMore, input, pageSize, connectionKey]);

  return {
    nodes,
    totalCount,
    loading,
    loadingMore,
    hasMore,
    loadMoreFailed,
    error,
    loadMore,
    refetch,
  };
}
